// Lightweight, fail-open creative metadata extraction for Creative Studio
// uploads. Reads intrinsic image/video dimensions (and video duration) in the
// browser BEFORE upload so the backend can persist them
// (generation_meta.metadata) for Eden saved-asset specs and the Platform
// column. No platform APIs, no heavy processing, never blocks the upload:
// every failure path resolves with the partial metadata + a limitation, and a
// timeout guards against a hung decode. Object URLs are always revoked.

const DEFAULT_TIMEOUT_MS = 5000;

const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "m4v", "avi", "mkv"];
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff", "svg"];
const DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx", "ppt", "pptx"];

const extensionOf = (name) => {
  const m = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? (m[1] === "jpeg" ? "jpg" : m[1]) : null;
};

const kindOf = (file, extension) => {
  const mime = String(file?.type || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("application/") || mime.startsWith("text/")) return "document";
  if (extension && IMAGE_EXTENSIONS.includes(extension)) return "image";
  if (extension && VIDEO_EXTENSIONS.includes(extension)) return "video";
  if (extension && DOCUMENT_EXTENSIONS.includes(extension)) return "document";
  return "unknown";
};

const createObjectUrl = (file) => {
  try {
    return typeof URL !== "undefined" && URL.createObjectURL ? URL.createObjectURL(file) : null;
  } catch (_err) {
    return null;
  }
};
const revokeObjectUrl = (url) => {
  try {
    if (url && typeof URL !== "undefined" && URL.revokeObjectURL) URL.revokeObjectURL(url);
  } catch (_err) {
    /* no-op */
  }
};

function extractImageDimensions(file, timeoutMs) {
  return new Promise((resolve) => {
    const url = createObjectUrl(file);
    if (!url || typeof Image === "undefined") {
      resolve({ width: null, height: null, limitation: "image_dimensions_unavailable" });
      return;
    }
    let settled = false;
    const img = new Image();
    const done = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      revokeObjectUrl(url);
      resolve(result);
    };
    const timer = setTimeout(() => done({ width: null, height: null, limitation: "image_dimensions_unavailable" }), timeoutMs);
    img.onload = () => {
      const width = Number(img.naturalWidth) || null;
      const height = Number(img.naturalHeight) || null;
      done(width && height ? { width, height } : { width: null, height: null, limitation: "image_dimensions_unavailable" });
    };
    img.onerror = () => done({ width: null, height: null, limitation: "image_dimensions_unavailable" });
    img.src = url;
  });
}

function extractVideoMetadata(file, timeoutMs) {
  return new Promise((resolve) => {
    const url = createObjectUrl(file);
    const video = typeof document !== "undefined" && document.createElement ? document.createElement("video") : null;
    if (!url || !video) {
      resolve({ width: null, height: null, durationSeconds: null, limitation: "video_metadata_unavailable" });
      return;
    }
    let settled = false;
    const done = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      try { video.removeAttribute("src"); } catch (_err) { /* no-op */ }
      revokeObjectUrl(url);
      resolve(result);
    };
    const timer = setTimeout(() => done({ width: null, height: null, durationSeconds: null, limitation: "video_metadata_unavailable" }), timeoutMs);
    video.onloadedmetadata = () => {
      const width = Number(video.videoWidth) || null;
      const height = Number(video.videoHeight) || null;
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      done({ width, height, durationSeconds: duration });
    };
    video.onerror = () => done({ width: null, height: null, durationSeconds: null, limitation: "video_metadata_unavailable" });
    video.preload = "metadata";
    video.src = url;
  });
}

/**
 * Extract creative metadata from a File. Always resolves (never throws/rejects)
 * with a serializable payload safe to send to the backend upload route.
 */
export async function extractCreativeAssetMetadata(file, { timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  const extension = extensionOf(file?.name);
  const base = {
    width: null,
    height: null,
    durationSeconds: null,
    pageCount: null,
    mimeType: String(file?.type || "") || null,
    extension,
    sizeBytes: Number.isFinite(file?.size) && file.size > 0 ? file.size : null,
    extractionSource: "client_upload_preview",
    limitations: [],
  };

  if (!file) return base;

  try {
    const kind = kindOf(file, extension);
    if (kind === "image") {
      const dims = await extractImageDimensions(file, timeoutMs);
      base.width = dims.width;
      base.height = dims.height;
      if (dims.limitation) base.limitations.push(dims.limitation);
    } else if (kind === "video") {
      const meta = await extractVideoMetadata(file, timeoutMs);
      base.width = meta.width;
      base.height = meta.height;
      base.durationSeconds = meta.durationSeconds;
      if (meta.limitation) base.limitations.push(meta.limitation);
    } else if (kind === "document") {
      // Cheap page-count extraction is not available without a heavy parser.
      base.limitations.push("page_count_unavailable");
    }
  } catch (_err) {
    // Fail open — never block the upload on extraction.
    if (!base.limitations.includes("metadata_extraction_failed")) {
      base.limitations.push("metadata_extraction_failed");
    }
  }

  return base;
}

export async function appendCreativeAssetMetadataToFormData(formData, file, options = {}) {
  if (!formData || typeof formData.append !== "function") {
    return { appended: false, metadata: null };
  }

  const { fieldName = "metadata", ...extractOptions } = options || {};
  try {
    const metadata = await extractCreativeAssetMetadata(file, extractOptions);
    if (metadata) {
      formData.append(fieldName, JSON.stringify(metadata));
      return { appended: true, metadata };
    }
  } catch (_err) {
    // Fail open: revision/upload should still proceed and the backend will
    // clear stale intrinsic fields when metadata is unavailable.
  }
  return { appended: false, metadata: null };
}

export default extractCreativeAssetMetadata;
